import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ColorModeContext, tokens } from "../../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearUserData } from "../../store/slices/userSlice";
import {
  getNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../../services/notificationService";

const Topbar = ({ isSidebarOpen, setIsSidebar }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [notifications, setNotifications] = useState([]);
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const previewNotifications = useMemo(() => notifications.slice(0, 5), [notifications]);

  const loadNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const data = await getNotifications({ limit: 5 });
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("No se pudieron cargar las notificaciones", err);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleOpenNotifications = (event) => {
    setNotificationsAnchor(event.currentTarget);
    loadNotifications();
  };

  const handleCloseNotifications = () => setNotificationsAnchor(null);

  const handleNotificationClick = async (notification) => {
    if (!notification) return;
    const id = notification.id || notification._id;
    try {
      if (!notification.read && id) {
        await markNotificationAsRead(id);
        setNotifications((prev) =>
          prev.map((n) => (String(n.id || n._id) === String(id) ? { ...n, read: true } : n))
        );
      }
    } catch (err) {
      console.error("No se pudo marcar la notificación como leída", err);
    }

    if (notification.payload?.closureId) {
      navigate(`/turnos/${notification.payload.closureId}`);
      handleCloseNotifications();
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error("No se pudieron marcar todas las notificaciones", err);
    }
  };

  const handleSeeAllNotifications = () => {
    navigate("/notifications");
    handleCloseNotifications();
  };

  const formatNotificationDate = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } catch {
      return "";
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token"); // Eliminar token de autenticación
    localStorage.removeItem("userData");
    dispatch(clearUserData());
    navigate("/"); // Redirigir a la landing
  };

  const handleOpenProfile = () => {
    navigate("/user");
  };

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
      <Box display="flex" alignItems="center" gap={1}>
        {isMobile && (
          <IconButton onClick={() => setIsSidebar && setIsSidebar((prev) => !prev)}>
            {isSidebarOpen ? <CloseIcon /> : <MenuOutlinedIcon />}
          </IconButton>
        )}
      </Box>

      {/* ICONS */}
      <Box display="flex">
        <IconButton onClick={colorMode.toggleColorMode}>
          {theme.palette.mode === "dark" ? (
            <DarkModeOutlinedIcon />
          ) : (
          <LightModeOutlinedIcon />
          )}
        </IconButton>
        <IconButton onClick={handleOpenNotifications}>
          <Badge
            color="error"
            badgeContent={unreadCount > 99 ? "99+" : unreadCount}
            overlap="circular"
            invisible={!unreadCount}
          >
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>
        <IconButton onClick={handleOpenProfile}>
          <SettingsOutlinedIcon />
        </IconButton>
        <IconButton onClick={handleOpenProfile}>
          <PersonOutlinedIcon />
        </IconButton>
        <IconButton onClick={handleLogout} color="error">
          <LogoutOutlinedIcon />
        </IconButton>
      </Box>

      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleCloseNotifications}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: "92vw",
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
            border: `1px solid ${colors.grey[600]}`,
            boxShadow: theme.shadows[10],
            "& .MuiMenuItem-root": { padding: "12px 14px" },
          },
        }}
      >
        <Box
          px={2}
          py={1.5}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            borderBottom: `1px solid ${colors.grey[700]}`,
            background: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[300],
          }}
        >
          <Typography variant="subtitle1" fontWeight={800}>
            Notificaciones
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color={colors.grey[200]}>
              {unreadCount ? `${unreadCount} nuevas` : "Al día"}
            </Typography>
            <Typography
              variant="body2"
              color={unreadCount ? "secondary" : colors.grey[400]}
              sx={{ cursor: unreadCount ? "pointer" : "default", fontWeight: 700 }}
              onClick={unreadCount ? handleMarkAllRead : undefined}
            >
              Marcar todo
            </Typography>
          </Box>
        </Box>

        {loadingNotifications ? (
          <Box px={2} py={2} display="flex" alignItems="center" gap={1.5}>
            <CircularProgress size={20} />
            <Typography variant="body2" color={colors.grey[200]}>
              Cargando notificaciones...
            </Typography>
          </Box>
        ) : previewNotifications.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="Sin notificaciones" primaryTypographyProps={{ color: colors.grey[200] }} />
          </MenuItem>
        ) : (
          previewNotifications.map((n) => {
            const isUnread = !n.read;
            return (
              <MenuItem
                key={n.id || n._id}
                onClick={() => handleNotificationClick(n)}
                sx={{
                  alignItems: "flex-start",
                  whiteSpace: "normal",
                  backgroundColor: isUnread ? colors.primary[500] : "transparent",
                  borderLeft: `4px solid ${isUnread ? colors.greenAccent[400] : "transparent"}`,
                  transition: "background-color 120ms ease",
                  "&:hover": {
                    backgroundColor: colors.primary[500],
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: isUnread ? colors.greenAccent[400] : colors.grey[500],
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body1" fontWeight={isUnread ? 800 : 600} color={colors.grey[100]}>
                        {n.title}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
                      <Typography variant="body2" color={colors.grey[200]}>
                        {n.message}
                      </Typography>
                      <Typography variant="caption" color={colors.grey[300]}>
                        {formatNotificationDate(n.createdAt)}
                      </Typography>
                    </Box>
                  }
                />
              </MenuItem>
            );
          })
        )}
        <Box px={2} py={1.25} sx={{ borderTop: `1px solid ${colors.grey[700]}` }}>
          <Typography
            variant="body2"
            onClick={handleSeeAllNotifications}
            sx={{
              cursor: "pointer",
              fontWeight: 700,
              color: colors.greenAccent[400],
              textAlign: "center",
            }}
          >
            Ver todas las notificaciones
          </Typography>
        </Box>
      </Menu>
    </Box>
  );
};

export default Topbar;
